import { useEffect, useRef, useState } from 'react';
import { pb } from '../lib/pocketbase.js';
import { PRAXIS_TYP_LABEL, praxisAnzeige } from '../lib/praxis.js';

// Searchable Dropdown für die Auswahl einer Mutter-Praxis.
//
// Sucht nur unter Praxen, die selbst NICHT Filiale sind (mutter_praxis = "")
// — sonst hätten wir mehr als 2 Ebenen. Schließt zusätzlich die übergebenen
// excludeIds aus (typischerweise die eigene ID beim Bearbeiten).
//
// Debounce 250 ms, max. 10 Treffer. Server-Suche per pb.filter() →
// kein Filter-Injection-Risiko.
export default function MutterPraxisAutocomplete({
  value,
  onChange,
  excludeIds = [],
  disabled = false,
  placeholder = 'Praxis suchen …',
}) {
  const containerRef = useRef(null);
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState([]);
  const [selected, setSelected] = useState(null);

  // Wenn von außen eine ID übergeben wird, das passende Record laden,
  // damit wir Name/Ort anzeigen können.
  useEffect(() => {
    if (!value) {
      setSelected(null);
      return undefined;
    }
    if (selected?.id === value) return undefined;
    let cancelled = false;
    pb.collection('praxen')
      .getOne(value)
      .then((record) => {
        if (!cancelled) setSelected(record);
      })
      .catch(() => {
        if (!cancelled) setSelected(null);
      });
    return () => {
      cancelled = true;
    };
    // selected.id absichtlich nicht in der Dep-Liste — sonst Endlosschleife.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  useEffect(() => {
    const q = query.trim();
    if (!q) {
      setResults([]);
      return undefined;
    }
    let cancelled = false;
    const handle = setTimeout(async () => {
      setLoading(true);
      try {
        // Nur potenzielle Mütter: mutter_praxis = "" (Self-Relation in PB
        // ist intern TEXT; leerer String = nicht gesetzt).
        const filter = pb.filter(
          'mutter_praxis = "" && (name ~ {:q} || ort ~ {:q} || plz ~ {:q})',
          { q }
        );
        const resp = await pb.collection('praxen').getList(1, 10, {
          filter,
          sort: 'name',
        });
        if (cancelled) return;
        const filtered = resp.items.filter((p) => !excludeIds.includes(p.id));
        setResults(filtered);
        setOpen(true);
      } catch (err) {
        if (!cancelled) {
          console.error('Mutter-Praxis-Suche fehlgeschlagen', err);
          setResults([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, 250);
    return () => {
      cancelled = true;
      clearTimeout(handle);
    };
  }, [query, excludeIds]);

  useEffect(() => {
    function onClick(e) {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    }
    if (open) {
      document.addEventListener('mousedown', onClick);
      return () => document.removeEventListener('mousedown', onClick);
    }
    return undefined;
  }, [open]);

  function selectPraxis(praxis) {
    setSelected(praxis);
    setQuery('');
    setOpen(false);
    setResults([]);
    onChange?.(praxis.id);
  }

  function clearSelection() {
    setSelected(null);
    setQuery('');
    setResults([]);
    onChange?.(null);
  }

  // Wenn etwas gewählt ist → Chip-Darstellung mit X-Button.
  if (selected) {
    return (
      <div className="mt-1 flex items-center justify-between gap-2 rounded-md border border-sky-200 bg-sky-50 px-3 py-2">
        <div className="min-w-0">
          <div className="truncate text-sm font-medium text-sky-900">
            {praxisAnzeige(selected)}
          </div>
          <div className="truncate text-xs text-sky-700">
            {[
              PRAXIS_TYP_LABEL[selected.typ] ?? selected.typ,
              [selected.plz, selected.ort].filter(Boolean).join(' '),
            ]
              .filter(Boolean)
              .join(' · ')}
          </div>
        </div>
        {!disabled && (
          <button
            type="button"
            onClick={clearSelection}
            className="rounded-md px-2 py-1 text-xs font-medium text-sky-900 hover:bg-sky-100"
            aria-label="Mutter-Praxis entfernen"
          >
            Entfernen
          </button>
        )}
      </div>
    );
  }

  return (
    <div ref={containerRef} className="relative mt-1">
      <input
        type="text"
        className="input"
        placeholder={placeholder}
        value={query}
        disabled={disabled}
        onChange={(e) => setQuery(e.target.value)}
        onFocus={() => {
          if (query.trim() && results.length > 0) setOpen(true);
        }}
      />
      {open && query.trim().length > 0 && (
        <div className="absolute left-0 right-0 top-full z-30 mt-1 max-h-72 overflow-y-auto rounded-md border border-slate-200 bg-white shadow-lg">
          {loading && (
            <div className="px-3 py-2 text-sm text-slate-500">Suche …</div>
          )}
          {!loading && results.length === 0 && (
            <div className="px-3 py-2 text-sm text-slate-500">
              Keine passenden Praxen gefunden.
            </div>
          )}
          {!loading &&
            results.map((p) => (
              <button
                key={p.id}
                type="button"
                onClick={() => selectPraxis(p)}
                className="block w-full px-3 py-2 text-left hover:bg-primary-50"
              >
                <div className="text-sm font-medium text-slate-900">{praxisAnzeige(p)}</div>
                <div className="text-xs text-slate-500">
                  {[
                    PRAXIS_TYP_LABEL[p.typ] ?? p.typ,
                    [p.plz, p.ort].filter(Boolean).join(' '),
                  ]
                    .filter(Boolean)
                    .join(' · ')}
                </div>
              </button>
            ))}
        </div>
      )}
    </div>
  );
}
